import React, { useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PhoneNumberField, AddressFields, TextInput } from '../../components/form';
import { formatPhoneNumber, locationAddressSchema, logError, showToast } from '../../utils';
import { DealerLocation } from '../../types';
import { useAddLocationMutation } from './locations-api-slice';

interface AddShippingAddressDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (address: DealerLocation) => void;
  dealerId: number;
}

const AddLocationAddressDialog: React.FC<AddShippingAddressDialogProps> = ({
  open,
  onClose,
  onSave,
  dealerId,
}) => {
  const [addLocation, { isLoading: isSaving }] = useAddLocationMutation();

  // Use useMemo to prevent the defaultValues object from being recreated on each render
  const defaultValues: Omit<DealerLocation, 'id'> = useMemo(
    () => ({
      dealerId,
      name: '',
      address1: '',
      address2: null,
      city: '',
      state: '',
      zip: '',
      phone: null,
      fax: null,
      email: null,
    }),
    [dealerId]
  );

  const addAddressForm = useForm({
    resolver: zodResolver(locationAddressSchema),
    defaultValues,
    mode: 'onTouched',
  });

  // Get the form state to check for validation errors and isDirty status
  const { formState, reset } = addAddressForm;
  const { isValid, isDirty } = formState;

  // Reset form when dialog opens or closes
  useEffect(() => {
    if (open) {
      // Reset to default values when the dialog opens
      reset(defaultValues);
    }
  }, [open, reset, defaultValues]);

  const handleSave = addAddressForm.handleSubmit(async data => {
    try {
      const locationData: Omit<DealerLocation, 'id'> = {
        ...data,
        dealerId,
      };

      const result = await addLocation({ locationData }).unwrap();

      // Show success toast
      showToast('Shipping address added successfully', 'success');

      // Call onSave with the newly created address (including ID)
      onSave(result);

      // Reset form and close dialog
      reset(defaultValues);
      onClose();
    } catch (error) {
      logError('Failed to save location:', error);
    }
  });

  const handleClose = (_event?: unknown, reason?: string) => {
    if (reason !== 'backdropClick') {
      // Reset form when dialog is closed
      reset(defaultValues);
      onClose();
    }
  };

  // Button is disabled when:
  // 1. Form is saving (API call in progress)
  // 2. Form hasn't been modified (isDirty is false)
  // 3. Form has validation errors (isValid is false)
  const isSaveDisabled = isSaving || !isDirty || (isDirty && !isValid);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { width: '40vw' } }}
    >
      <DialogTitle>Add Shipping Address</DialogTitle>
      <FormProvider {...addAddressForm}>
        <form
          onSubmit={e => {
            e.stopPropagation();
            handleSave(e);
          }}
        >
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextInput
                  name="name"
                  control={addAddressForm.control}
                  label="Name"
                  required
                  isReadOnly={false}
                />
              </Grid>
              <AddressFields
                control={addAddressForm.control}
                address1Name="address1"
                address2Name="address2"
                cityName="city"
                stateName="state"
                zipName="zip"
              />
              <Grid item xs={12} sm={5}>
                <PhoneNumberField
                  name="phone"
                  control={addAddressForm.control}
                  formatPhoneNumber={formatPhoneNumber}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary" disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isSaveDisabled}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AddLocationAddressDialog;
